// Dependências
import Vue from 'vue';

// Estado inicial
const initialState = () => ({
  logs: [],
  total: 0,
  paginaAtual: 1,
  totalPorPagina: 20,
  loadingState: false,
  error: null,
});

// Objeto de estado
const state = initialState();

// Getters
const getters = {
  getLogsList(state) {
    return state.logs;
  },
  getTotal(state) {
    return state.total;
  },
  getPaginaAtual(state) {
    return state.paginaAtual;
  },
  getTotalPorPagina(state) {
    return state.totalPorPagina;
  },
  getPaginacao(state) {
    return {
      total: state.total,
      paginaAtual: state.paginaAtual,
      totalPorPagina: state.totalPorPagina,
      totalPaginas: Math.ceil(state.total / state.totalPorPagina)
    };
  },
  getLoadingState(state) {
    return state.loadingState;
  },
  getError(state) {
    return state.error;
  }
};

// Ações
const actions = {
  clearState({ commit }) {
    commit('RESET');
  },

  /**
   * Buscar logs de operações de uma configuração específica
   * @param {Object} context - contexto do Vuex
   * @param {Object} params - parâmetros da busca
   * @param {string} params.entidadeId - ID da configuração
   * @param {number} params.pagina - Página atual (opcional, padrão: 1)
   * @param {number} params.totalPorPagina - Itens por página (opcional, padrão: 20)
   */
  fetchLogsPorConfiguracao({ commit }, { entidadeId, pagina = 1, totalPorPagina = 20 }) {
    return new Promise(async (resolve) => {
      if (!entidadeId) {
        commit('SET_ERROR', 'ID da configuração é obrigatório');
        resolve({
          status: 400,
          msg: 'ID da configuração é obrigatório'
        });
        return;
      }

      commit('SET_LOADING_STATE', true);
      commit('CLEAR_ERROR');

      try {
        const api = new Vue.prototype.$httpClient();
        
        // Construir query parameters
        const params = new URLSearchParams();
        params.append('entidadeId', entidadeId);
        params.append('pagina', pagina.toString());
        params.append('totalPorPagina', totalPorPagina.toString());
        
        const url = `/logs/operacoes?${params.toString()}`;
        
        const response = await api.send(url, 'GET', null, undefined, false);
        
        if (response.status === 200) {
          commit('SET_LOGS', response.data.dados.logs || []);
          commit('SET_TOTAL', response.data.dados.paginacao?.total || 0);
          commit('SET_PAGINA_ATUAL', pagina);
          commit('SET_TOTAL_POR_PAGINA', totalPorPagina);
          
          resolve({
            status: 200,
            msg: 'Logs recuperados com sucesso',
            dados: response.data.dados
          });
        } else {
          commit('SET_LOGS', []);
          commit('SET_TOTAL', 0);
          commit('SET_ERROR', response.data?.mensagem || 'Erro ao buscar logs');
          
          resolve({
            status: response.status,
            msg: response.data?.mensagem || 'Erro ao buscar logs'
          });
        }
      } catch (error) {
        console.error('[LOGS] Erro ao buscar logs:', error);
        commit('SET_LOGS', []);
        commit('SET_TOTAL', 0);
        commit('SET_ERROR', error.message || 'Erro interno');
        
        resolve({
          status: 500,
          msg: error.message || 'Erro interno'
        });
      } finally {
        commit('SET_LOADING_STATE', false);
      }
    });
  },

  /**
   * Alterar página atual
   * @param {Object} context - contexto do Vuex
   * @param {number} pagina - Nova página
   */
  setPaginaAtual({ commit, state }, pagina) {
    commit('SET_PAGINA_ATUAL', pagina);
  },

  /**
   * Alterar total por página
   * @param {Object} context - contexto do Vuex
   * @param {number} totalPorPagina - Novo total por página
   */
  setTotalPorPagina({ commit }, totalPorPagina) {
    commit('SET_TOTAL_POR_PAGINA', totalPorPagina);
  }
};

// Mutações
const mutations = {
  RESET(state) {
    const newState = initialState();
    Object.keys(newState).forEach(key => {
      state[key] = newState[key];
    });
  },
  
  SET_LOGS(state, logs) {
    state.logs = logs;
  },
  
  SET_TOTAL(state, total) {
    state.total = total;
  },
  
  SET_PAGINA_ATUAL(state, pagina) {
    state.paginaAtual = pagina;
  },
  
  SET_TOTAL_POR_PAGINA(state, totalPorPagina) {
    state.totalPorPagina = totalPorPagina;
  },
  
  SET_LOADING_STATE(state, loading) {
    state.loadingState = loading;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  CLEAR_ERROR(state) {
    state.error = null;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};

